import { useState, useEffect } from 'react';
import { Download, X, Smartphone, Share2 } from 'lucide-react';
import { Button } from './ui/button';

/**
 * PWA Install Prompt Component
 * 
 * Shows a native-feeling "Add to Home Screen" prompt on mobile devices.
 * Handles both iOS (manual instructions) and Android (native prompt).
 */
const PWAInstallPrompt = () => {
  const [showPrompt, setShowPrompt] = useState(false);
  const [deferredPrompt, setDeferredPrompt] = useState(null);
  const [isIOS, setIsIOS] = useState(false);
  const [isStandalone, setIsStandalone] = useState(false);

  useEffect(() => {
    // Check if already installed as PWA
    const standalone = window.matchMedia('(display-mode: standalone)').matches 
      || window.navigator.standalone 
      || document.referrer.includes('android-app://');
    
    setIsStandalone(standalone);
    
    if (standalone) {
      return; // Don't show prompt if already installed
    }

    // Check if iOS
    const iOS = /iPad|iPhone|iPod/.test(navigator.userAgent) && !window.MSStream;
    setIsIOS(iOS);

    // Check if user has dismissed before
    const dismissed = localStorage.getItem('pwa-prompt-dismissed');
    const dismissedTime = dismissed ? parseInt(dismissed, 10) : 0;
    const oneWeek = 7 * 24 * 60 * 60 * 1000;
    
    if (dismissedTime && Date.now() - dismissedTime < oneWeek) {
      return; // Don't show if dismissed within last week
    }

    // For Android/Chrome - capture the beforeinstallprompt event
    const handleBeforeInstallPrompt = (e) => {
      e.preventDefault();
      setDeferredPrompt(e);
      
      // Show prompt after a delay (let user explore first)
      setTimeout(() => {
        setShowPrompt(true);
      }, 3000);
    };

    window.addEventListener('beforeinstallprompt', handleBeforeInstallPrompt);

    // For iOS - show manual instructions after delay
    if (iOS && !standalone) {
      setTimeout(() => {
        setShowPrompt(true);
      }, 5000);
    }

    return () => {
      window.removeEventListener('beforeinstallprompt', handleBeforeInstallPrompt);
    };
  }, []);

  const handleInstallClick = async () => {
    if (deferredPrompt) {
      // Show native install prompt (Android/Chrome)
      deferredPrompt.prompt();
      
      const { outcome } = await deferredPrompt.userChoice;
      console.log(`[PWA] User ${outcome === 'accepted' ? 'accepted' : 'dismissed'} install`);
      
      setDeferredPrompt(null);
      setShowPrompt(false);
      
      if (outcome === 'accepted') {
        localStorage.setItem('pwa-installed', 'true');
      }
    }
  };

  const handleDismiss = () => {
    setShowPrompt(false);
    localStorage.setItem('pwa-prompt-dismissed', Date.now().toString());
  };

  if (!showPrompt || isStandalone) {
    return null;
  }

  return (
    <div 
      className="fixed bottom-20 left-4 right-4 z-50 animate-in slide-in-from-bottom duration-500"
      data-testid="pwa-install-prompt"
    >
      <div 
        className="bg-card/95 backdrop-blur-xl border border-primary/30 rounded-2xl p-4 shadow-2xl"
        style={{ boxShadow: '0 0 40px rgba(220, 38, 38, 0.15)' }}
      >
        {/* Close button */}
        <button
          onClick={handleDismiss}
          className="absolute top-3 right-3 w-8 h-8 rounded-full bg-muted/50 flex items-center justify-center text-muted-foreground hover:text-foreground transition-colors"
          aria-label="Dismiss"
        >
          <X className="w-4 h-4" />
        </button>

        <div className="flex items-start gap-4">
          {/* Icon */}
          <div className="w-14 h-14 rounded-xl bg-primary/20 border border-primary/50 flex items-center justify-center flex-shrink-0">
            <Smartphone className="w-7 h-7 text-primary" />
          </div>

          {/* Content */}
          <div className="flex-1 min-w-0">
            <h3 className="font-heading font-bold text-foreground text-lg mb-1">
              Add XTrip to Home Screen
            </h3>
            
            {isIOS ? (
              // iOS Instructions
              <div className="space-y-2">
                <p className="text-sm text-muted-foreground">
                  Install XTrip for the best experience:
                </p>
                <div className="flex items-center gap-2 text-sm text-muted-foreground bg-muted/30 rounded-lg p-2">
                  <Share2 className="w-4 h-4 text-primary flex-shrink-0" />
                  <span>
                    Tap <span className="text-primary font-medium">Share</span> then{' '}
                    <span className="text-primary font-medium">"Add to Home Screen"</span>
                  </span>
                </div>
                <Button
                  onClick={handleDismiss}
                  variant="outline"
                  size="sm"
                  className="mt-2 border-primary/50 text-primary hover:bg-primary/10"
                >
                  Got it
                </Button>
              </div>
            ) : (
              // Android/Chrome Install Button
              <div className="space-y-2">
                <p className="text-sm text-muted-foreground">
                  Quick access to events, offline support, and native app feel.
                </p>
                <div className="flex gap-2 mt-3">
                  <Button
                    onClick={handleInstallClick}
                    size="sm"
                    className="bg-primary hover:bg-primary/90 text-primary-foreground font-bold"
                    data-testid="install-pwa-btn"
                  >
                    <Download className="w-4 h-4 mr-2" />
                    Install App
                  </Button>
                  <Button
                    onClick={handleDismiss}
                    variant="ghost"
                    size="sm"
                    className="text-muted-foreground hover:text-foreground"
                  >
                    Not now
                  </Button>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default PWAInstallPrompt;
